import EmailIcon from "@mui/icons-material/Email";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SubmitHandler, useForm } from "react-hook-form";

import { trimStringInputs } from "../helpers/form";
import { stripSpaces } from "../helpers/string";
import { APPage, CRMAppLocationIdentifier } from "../types/Faq";
import PhoneButton from "./buttons/PhoneButton";
import FaqList from "./faq/FaqList";
import APForm from "./form/APForm";
import APTextField from "./form/APTextField";

type Inputs = {
  message: string;
};

export type APHelpFormConfig = {
  title: string;
  bio: string;
  phone: string;
  callText: string;
  messageText?: string;
  image?: string;
  faq?: {
    pageId: APPage;
    appLocationIdentifier: CRMAppLocationIdentifier;
    opportunityId?: string;
  };
};

const APHelpForm = ({
  config,
  onSubmit,
}: {
  config: APHelpFormConfig;
  onSubmit: (message: string) => void;
}) => {
  const { title, bio, phone, callText, messageText, image, faq } = config;

  const { control, handleSubmit, setValue, getValues } = useForm<Inputs>({
    mode: "onTouched",
  });

  const _onSubmit: SubmitHandler<Inputs> = async (_data) => {
    trimStringInputs(_data, setValue);
    const data = getValues();

    onSubmit(data.message);
  };

  return (
    <>
      {/* Heading */}
      <Typography variant="headlineLarge">{title}</Typography>

      {/* Profile and bio */}
      <Box
        sx={{
          mt: 2,
          "::after": { content: "''", display: "block", clear: "both" },
        }}
      >
        <Box sx={{ width: 160, height: 160, float: "left" }} mb={3} mr={1}>
          {image && (
            <Box component="img" src={image} sx={{ width: 160, height: 160 }} />
          )}
        </Box>
        <Box pt={1} mb={3}>
          <Typography variant="labelLarge" sx={{ whiteSpace: "pre-line" }}>
            {bio}
          </Typography>
        </Box>
      </Box>

      {/* Contact options */}
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <PhoneButton href={`tel:${stripSpaces(phone)}`} />
          <a href={`tel:${stripSpaces(phone)}`}>
            <Typography variant="bodyLarge">
              {callText} {phone}
            </Typography>
          </a>
        </Stack>
        {messageText && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "100%",
                }}
              >
                <EmailIcon fontSize="small" />
              </Box>{" "}
              <Typography variant="bodyLarge">{messageText}</Typography>
            </Stack>
            {/* Message form */}
            <APForm
              onSubmit={handleSubmit(_onSubmit)}
              submitText="Send"
              isLoading={false}
              submitButtonColor="primary"
              minSpacing
            >
              <APTextField
                name="message"
                label="Message"
                control={control}
                validations={{ required: true }}
                errors={{}}
                defaultValue={""}
                placeholder="Type your message here..."
                helperText={""}
                multiline
                minRows={5}
              />
            </APForm>
          </Box>
        )}

        {faq && (
          <FaqList
            pageId={faq.pageId}
            appLocationIdentifier={faq.appLocationIdentifier}
            faqContextIds={{
              opportunityId: faq.opportunityId,
            }}
          />
        )}
      </Stack>
    </>
  );
};

export default APHelpForm;
